import type { MoneyValue } from '@akademi/site-builder-runtime';

/**
 * Which tax basis a price is shown on: including VAT or excluding it.
 *
 * <p>The rule the shop's owner set: <strong>the public always sees prices including
 * VAT</strong>; a signed-in, approved trade customer may switch between inc and ex VAT,
 * and starts on inc. The choice is kept in `localStorage` under {@link VAT_DISPLAY_KEY}
 * as `"inc"` or `"ex"`. Other pages of the shop on the same origin read the same key,
 * so its name and values are a contract, not an implementation detail.
 *
 * <p><strong>Nothing here does tax arithmetic.</strong> Every price arrives from the
 * backend with its published figure and, where the backend could resolve the product's
 * tax, the same price on the other basis (`alternate`). Choosing a basis only picks
 * which of the two backend figures to show. When the other figure is missing the
 * published one is shown with its own label — an ex VAT figure honestly labelled beats
 * an inc VAT figure guessed in the browser.
 */

/** The `localStorage` key the chosen basis is kept under. */
export const VAT_DISPLAY_KEY = 'sorsy.vat-display';

export type VatBasis = 'inc' | 'ex';

/**
 * What every price on a page needs to know about the shopper's tax display, as a VALUE.
 *
 * <p>Separated from the hook that produces it ({@link useVatDisplay}) because a hook cannot
 * leave the browser: a component chosen on the server — a vertical's own product grid — is
 * handed this instead, read once by the client wrapper that renders it. `basis` is already
 * the basis to SHOW, {@link shownBasis} having been applied, so a reader never has to combine
 * the two itself and can never show a visitor the trade view by forgetting to.
 */
export interface VatDisplayState {
  /** Whether this shopper is an approved trade customer, and so may switch at all. */
  trade: boolean;
  /** The basis prices are shown on: always `inc` unless {@link trade}. */
  basis: VatBasis;
}

/** The same price on the other tax basis, as the backend sends it. */
export interface MoneyBasis {
  amount: string;
  taxInclusive: boolean;
}

/** A price as this storefront carries it: the runtime's money, plus its other basis. */
export type PricedMoney = MoneyValue & { alternate?: MoneyBasis };

type StorageSource = () => Pick<Storage, 'getItem' | 'setItem'> | null | undefined;

function browserStorage(): Storage | null {
  return typeof window === 'undefined' ? null : window.localStorage;
}

/**
 * The stored choice, `"inc"` unless `"ex"` was stored. Never throws: storage that is
 * missing, blocked or broken (private windows, disabled site data) reads as the default.
 */
export function readVatDisplay(storage: StorageSource = browserStorage): VatBasis {
  try {
    return storage()?.getItem(VAT_DISPLAY_KEY) === 'ex' ? 'ex' : 'inc';
  } catch {
    return 'inc';
  }
}

/** Stores the choice. Never throws; returns whether it was stored. */
export function writeVatDisplay(basis: VatBasis, storage: StorageSource = browserStorage): boolean {
  try {
    const target = storage();
    if (!target) return false;
    target.setItem(VAT_DISPLAY_KEY, basis);
    return true;
  } catch {
    return false;
  }
}

/** The basis actually shown: a trade customer's choice, and inc VAT for everyone else. */
export function shownBasis(trade: boolean, chosen: VatBasis): VatBasis {
  return trade ? chosen : 'inc';
}

/**
 * `value` on `basis`: the published figure when it is already on that basis, else the
 * backend's `alternate` when it is, else the published figure unchanged — label and all.
 */
export function onBasis(value: PricedMoney, basis: VatBasis): MoneyValue {
  const inclusive = basis === 'inc';
  if (!!value.taxInclusive === inclusive) return value;
  const alternate = value.alternate;
  if (alternate && typeof alternate.amount === 'string' && alternate.taxInclusive === inclusive) {
    return { amount: alternate.amount, currency: value.currency, taxInclusive: inclusive };
  }
  return value;
}

/**
 * Whether the caller is priced as an approved trade customer, from
 * `GET /api/storefront/prices`. Anything but a clear `true` — a visitor, an error, a
 * network failure — is `false`, which shows inc VAT: the public default.
 */
export async function fetchTradeStatus(
  url: string,
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  try {
    const response = await fetchImpl(url, { cache: 'no-store', credentials: 'same-origin' });
    if (!response.ok) return false;
    const body = (await response.json()) as { trade?: unknown } | null;
    return body?.trade === true;
  } catch {
    return false;
  }
}
